import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { ArrowRight, ArrowLeft, RotateCcw } from "lucide-react";
import { ASSESSMENT } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Flatten questions with their dimension reference.
const QUESTIONS = ASSESSMENT.dimensions.flatMap((d, di) =>
  d.questions.map((q, qi) => ({ dim: di, key: d.key, label: d.label, q, id: `${di}-${qi}` }))
);

function tierFor(score) {
  return ASSESSMENT.tiers.find((t) => score >= t.min && score <= t.max) || ASSESSMENT.tiers[0];
}

export default function ReadinessAssessment() {
  const [step, setStep] = useState(-1); // -1 intro, 0..n questions, n results
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const total = QUESTIONS.length;
  const inResults = step >= total;

  const dimScores = useMemo(() => {
    return ASSESSMENT.dimensions.map((d, di) => {
      const qs = QUESTIONS.filter((q) => q.dim === di);
      const vals = qs.map((q) => answers[q.id]).filter((v) => v != null);
      const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
      return { dimension: d.label, key: d.key, score: Math.round(avg * 10) / 10, di };
    });
  }, [answers]);

  const overall = useMemo(() => {
    const scored = dimScores.filter((d) => d.score > 0);
    if (!scored.length) return 0;
    return Math.round((scored.reduce((a, b) => a + b.score, 0) / scored.length) * 10) / 10;
  }, [dimScores]);

  const tier = tierFor(overall);

  const recommendations = dimScores.map((d) => {
    const dim = ASSESSMENT.dimensions[d.di];
    const rec = d.score < 3 ? dim.recommendations.low : dim.recommendations.high;
    return { label: d.label, score: d.score, rec, maps: dim.maps };
  });

  const allAnswered = QUESTIONS.every((q) => answers[q.id] != null);

  const sendSummary = async () => {
    if (!email) return;
    try {
      await fetch(`${API}/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name: `Readiness summary — ${tier.name} (${overall})` }),
      });
      setEmailSent(true);
    } catch {
      setEmailSent(true); // still confirm; capture is best-effort
    }
  };

  // Intro
  if (step === -1) {
    return (
      <div className="m61-card p-8 lg:p-10" data-testid="assessment-intro">
        <h3 className="m61-h3 mb-3">How ready are you for the convergence?</h3>
        <p className="text-m61-muted mb-6 max-w-2xl">
          A short, confidential self-assessment across five dimensions — strategy &amp; sovereignty,
          governance &amp; accountability, risk &amp; assurance, trust &amp; ethics, and sustainability
          &amp; resilience — mapped to the standards that matter. It runs entirely in your browser; nothing
          is stored unless you ask for an emailed summary.
        </p>
        <button className="m61-btn-primary" onClick={() => setStep(0)} data-testid="assessment-begin">
          Begin the assessment <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Results
  if (inResults) {
    const chartData = dimScores.map((d) => ({ dimension: d.dimension.split(" & ")[0], score: d.score }));
    return (
      <div className="m61-card p-8 lg:p-10" data-testid="assessment-results">
        <p className="m61-eyebrow mb-4">Your maturity profile</p>
        <div className="flex flex-wrap items-baseline gap-3 mb-6">
          <span className="font-display text-5xl m61-gradient-text">{overall.toFixed(1)}</span>
          <span className="font-display text-2xl text-m61-text">{tier.name}</span>
        </div>
        <p className="text-m61-muted max-w-2xl mb-8">{tier.blurb}</p>

        <div className="w-full h-72 mb-8" data-testid="assessment-radar">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData} outerRadius="75%">
              <PolarGrid stroke="#1E2740" />
              <PolarAngleAxis dataKey="dimension" tick={{ fill: "#8C97B5", fontSize: 11 }} />
              <PolarRadiusAxis domain={[0, 5]} tick={{ fill: "#8C97B5", fontSize: 10 }} stroke="#1E2740" />
              <Radar dataKey="score" stroke="#46E0D0" fill="#46E0D0" fillOpacity={0.3} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <h4 className="m61-h3 mb-4">Tailored recommendations</h4>
        <ul className="space-y-4 mb-8">
          {recommendations.map((r) => (
            <li key={r.label} className="border-l-2 border-m61-line-strong pl-4">
              <div className="flex items-center gap-3 mb-1">
                <span className="font-display text-m61-text">{r.label}</span>
                <span className="font-mono text-xs text-m61-cyan">{r.score.toFixed(1)} / 5</span>
              </div>
              <p className="text-m61-muted text-sm">{r.rec}</p>
              <p className="text-m61-muted/70 text-xs font-mono mt-1">Maps to: {r.maps}</p>
            </li>
          ))}
        </ul>

        <div className="border-t border-m61-line pt-6 flex flex-col sm:flex-row gap-4 sm:items-end justify-between">
          <div className="max-w-sm">
            <label className="block text-sm text-m61-text mb-2">Email me a summary <span className="text-m61-muted">(optional)</span></label>
            {emailSent ? (
              <p className="text-m61-cyan text-sm" data-testid="assessment-email-sent">Thanks — we'll send your summary shortly.</p>
            ) : (
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="flex-1 bg-m61-bg border border-m61-line rounded-lg px-3 py-2 text-m61-text placeholder:text-m61-muted/60 focus:border-m61-cyan focus:outline-none"
                  data-testid="assessment-email-input"
                />
                <button className="m61-btn-ghost" onClick={sendSummary} data-testid="assessment-email-send">Send</button>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <button className="m61-btn-ghost" onClick={() => { setStep(-1); setAnswers({}); setEmail(""); setEmailSent(false); }} data-testid="assessment-restart">
              <RotateCcw className="w-4 h-4" /> Start over
            </button>
            <Link to="/contact" className="m61-btn-primary" data-testid="assessment-discuss">
              Discuss your results <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Question step
  const current = QUESTIONS[step];
  const progress = Math.round((step / total) * 100);

  return (
    <div className="m61-card p-8 lg:p-10" data-testid="assessment-question">
      <div className="flex items-center justify-between mb-6">
        <span className="font-mono text-xs text-m61-muted uppercase tracking-wider">{current.label}</span>
        <span className="font-mono text-xs text-m61-muted">{step + 1} / {total}</span>
      </div>
      <div className="h-1 bg-m61-line rounded-full mb-8 overflow-hidden">
        <div className="h-full bg-m61-cyan transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>

      <p className="text-lg text-m61-text mb-8">{current.q}</p>

      <div className="space-y-3 mb-8">
        {ASSESSMENT.levels.map((lvl, i) => {
          const val = i + 1;
          const selected = answers[current.id] === val;
          return (
            <button
              key={lvl}
              onClick={() => setAnswers({ ...answers, [current.id]: val })}
              className={`w-full text-left px-4 py-3 rounded-lg border transition-colors flex items-center gap-3 ${
                selected ? "border-m61-cyan bg-m61-cyan/10 text-m61-text" : "border-m61-line text-m61-muted hover:border-m61-line-strong"
              }`}
              data-testid={`assessment-option-${val}`}
            >
              <span className={`w-6 h-6 rounded-full border flex items-center justify-center text-xs font-mono ${selected ? "border-m61-cyan text-m61-cyan" : "border-m61-line-strong"}`}>{val}</span>
              {lvl}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between">
        <button
          className="m61-btn-ghost disabled:opacity-40"
          onClick={() => setStep(step - 1)}
          disabled={step === 0}
          data-testid="assessment-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {step < total - 1 ? (
          <button
            className="m61-btn-primary disabled:opacity-40"
            onClick={() => setStep(step + 1)}
            disabled={answers[current.id] == null}
            data-testid="assessment-next"
          >
            Next <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            className="m61-btn-primary disabled:opacity-40"
            onClick={() => setStep(total)}
            disabled={!allAnswered}
            data-testid="assessment-see-results"
          >
            See results <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
